import { useEffect, useMemo, useState } from "react";
import {
  EVENT_TYPE_LABELS,
  EventType,
  packetMatchesFilters,
  type TrapPacketDto,
  type TrapQueryDto,
} from "@gryzonie/shared";
import { useTrapPackets } from "../hooks/useTrapPackets";
import { useTrapSocket } from "../hooks/useTrapSocket";
import { TrapTable } from "../components/TrapTable";
import { RowLimitControl } from "../components/RowLimitControl";

type StringInput = string;

interface FilterState {
  search: StringInput;
  eventType: StringInput;
  pid: StringInput;
  tempCMin: StringInput;
  tempCMax: StringInput;
  humidityMin: StringInput;
  humidityMax: StringInput;
  batteryVMin: StringInput;
  batteryVMax: StringInput;
  eventCountMin: StringInput;
  eventCountMax: StringInput;
}

const EMPTY_FILTERS: FilterState = {
  search: "",
  eventType: "",
  pid: "",
  tempCMin: "",
  tempCMax: "",
  humidityMin: "",
  humidityMax: "",
  batteryVMin: "",
  batteryVMax: "",
  eventCountMin: "",
  eventCountMax: "",
};

function toNum(v: string): number | undefined {
  if (v.trim() === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function buildQuery(f: FilterState, limit: number): TrapQueryDto {
  return {
    limit,
    search: f.search.trim() || undefined,
    eventType: toNum(f.eventType),
    pid: toNum(f.pid),
    tempCMin: toNum(f.tempCMin),
    tempCMax: toNum(f.tempCMax),
    humidityMin: toNum(f.humidityMin),
    humidityMax: toNum(f.humidityMax),
    batteryVMin: toNum(f.batteryVMin),
    batteryVMax: toNum(f.batteryVMax),
    eventCountMin: toNum(f.eventCountMin),
    eventCountMax: toNum(f.eventCountMax),
  };
}

export function DashboardPage() {
  const [limit, setLimit] = useState(100);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [livePackets, setLivePackets] = useState<TrapPacketDto[]>([]);
  const [status, setStatus] = useState<"connecting" | "connected" | "disconnected">("connecting");
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    const stored = localStorage.getItem("gryzonie.autoRefresh");
    return stored === null ? true : stored === "1";
  });

  useEffect(() => {
    localStorage.setItem("gryzonie.autoRefresh", autoRefresh ? "1" : "0");
  }, [autoRefresh]);

  const query = useMemo(() => buildQuery(filters, limit), [filters, limit]);
  const { data, isLoading, error } = useTrapPackets(query, autoRefresh);

  // Reset live buffer whenever the underlying query changes (filters/limit).
  useEffect(() => {
    setLivePackets([]);
  }, [query, data]);

  useTrapSocket({
    onConnectionChange: setStatus,
    onPacket: (p) => {
      if (!autoRefresh) return;
      // Apply current filters client-side so the live row is consistent.
      if (!packetMatchesFilters(p, query)) return;
      setLivePackets((prev) => [p, ...prev].slice(0, limit));
    },
  });

  const merged = useMemo(() => {
    const baseline = data ?? [];
    const seen = new Set<number>();
    const combined: TrapPacketDto[] = [];
    for (const p of livePackets) {
      if (p.id !== undefined) seen.add(p.id);
      combined.push(p);
    }
    for (const p of baseline) {
      if (p.id !== undefined && seen.has(p.id)) continue;
      combined.push(p);
    }
    return combined.slice(0, limit);
  }, [livePackets, data, limit]);

  const set = <K extends keyof FilterState>(k: K, v: FilterState[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const activeFilterCount = Object.entries(filters).filter(([, v]) => v !== "").length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Pulpit</h1>
          <p className="text-sm text-subtext0">
            Status WebSocket:{" "}
            <span
              className={
                status === "connected"
                  ? "text-success"
                  : status === "connecting"
                    ? "text-warning"
                    : "text-danger"
              }
            >
              {status}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <RowLimitControl value={limit} onChange={setLimit} />
          <label className="flex cursor-pointer items-center gap-2 text-xs text-subtext1">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="h-4 w-4 cursor-pointer accent-primary"
            />
            Auto-odświeżanie
          </label>
        </div>
      </div>

      <div className="rounded border border-surface0 bg-mantle p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-subtext0">
            Filtry {activeFilterCount > 0 && (
              <span className="ml-1 rounded bg-primary/20 px-1.5 py-0.5 text-[10px] text-primary">
                {activeFilterCount} aktywne
              </span>
            )}
          </h2>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="text-xs text-subtext0 underline-offset-2 hover:text-text hover:underline"
            >
              Wyczyść wszystkie
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Device ID (zawiera)">
            <input
              value={filters.search}
              onChange={(e) => set("search", e.target.value)}
              placeholder="hex device id…"
              className={inputCls}
            />
          </Field>

          <Field label="Typ zdarzenia">
            <select
              value={filters.eventType}
              onChange={(e) => set("eventType", e.target.value)}
              className={inputCls}
            >
              <option value="">— wszystkie —</option>
              {Object.values(EventType).map((code) => (
                <option key={code} value={code}>
                  0x{code.toString(16).padStart(2, "0")} — {EVENT_TYPE_LABELS[code as EventType]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="PID (dokładnie)">
            <input
              type="number"
              min={0}
              max={255}
              value={filters.pid}
              onChange={(e) => set("pid", e.target.value)}
              placeholder="0-255"
              className={inputCls}
            />
          </Field>

          <RangeField
            label="Temperatura [°C]"
            step="0.1"
            min={filters.tempCMin}
            max={filters.tempCMax}
            onMin={(v) => set("tempCMin", v)}
            onMax={(v) => set("tempCMax", v)}
            placeholderMin="np. 18.0"
            placeholderMax="np. 26.5"
          />

          <RangeField
            label="Wilgotność [%]"
            step="1"
            min={filters.humidityMin}
            max={filters.humidityMax}
            onMin={(v) => set("humidityMin", v)}
            onMax={(v) => set("humidityMax", v)}
            placeholderMin="0"
            placeholderMax="100"
          />

          <RangeField
            label="Bateria [V]"
            step="0.01"
            min={filters.batteryVMin}
            max={filters.batteryVMax}
            onMin={(v) => set("batteryVMin", v)}
            onMax={(v) => set("batteryVMax", v)}
            placeholderMin="2.0"
            placeholderMax="3.0"
          />

          <RangeField
            label="Liczba zdarzeń"
            step="1"
            min={filters.eventCountMin}
            max={filters.eventCountMax}
            onMin={(v) => set("eventCountMin", v)}
            onMax={(v) => set("eventCountMax", v)}
            placeholderMin="0"
            placeholderMax="20"
          />
        </div>
      </div>

      {error && (
        <div className="rounded border border-danger bg-surface0 p-3 text-sm text-danger">
          Nie udało się pobrać danych: {(error as Error).message}
        </div>
      )}

      <TrapTable packets={merged} isLoading={isLoading} />
    </div>
  );
}

const inputCls =
  "mt-1 w-full rounded border border-surface1 bg-surface0 px-2 py-1 text-sm text-text outline-none focus:border-primary";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col text-xs text-subtext1">
      {label}
      {children}
    </label>
  );
}

function RangeField({
  label,
  step,
  min,
  max,
  onMin,
  onMax,
  placeholderMin,
  placeholderMax,
}: {
  label: string;
  step: string;
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  placeholderMin: string;
  placeholderMax: string;
}) {
  return (
    <div className="flex flex-col text-xs text-subtext1">
      <span>{label}</span>
      <div className="mt-1 flex items-center gap-1">
        <input
          type="number"
          step={step}
          value={min}
          onChange={(e) => onMin(e.target.value)}
          placeholder={placeholderMin}
          className="w-full rounded border border-surface1 bg-surface0 px-2 py-1 text-sm text-text outline-none focus:border-primary"
        />
        <span className="text-overlay0">–</span>
        <input
          type="number"
          step={step}
          value={max}
          onChange={(e) => onMax(e.target.value)}
          placeholder={placeholderMax}
          className="w-full rounded border border-surface1 bg-surface0 px-2 py-1 text-sm text-text outline-none focus:border-primary"
        />
      </div>
    </div>
  );
}
